import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/simple-select";
import { Button } from "@/components/ui/button";
import { Globe2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CURRENCY_OPTIONS = [
  { value: "CHF", label: "CHF" },
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
  { value: "JPY", label: "JPY" },
];

export function FxRatesWidget() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("CHF");
  const [converted, setConverted] = useState(null);

  const loadRates = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/fx/rates");
      setRates(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "FX-Rates nicht verfügbar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRates();
  }, []);

  const convert = async (e) => {
    e?.preventDefault?.();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Bitte Betrag eingeben.");
      return;
    }
    try {
      const r = await apiClient.get("/fx/convert", {
        params: { amount: amt, from_currency: from, to_currency: to },
      });
      setConverted(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Umrechnung fehlgeschlagen");
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="fx-rates-widget">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Globe2 className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-900">Wechselkurse</h3>
            <p className="text-xs text-slate-500">
              Tageskurse {rates?.date ? `vom ${formatDate(rates.date)}` : ""} (Basis CHF, Quelle: exchangerate.host)
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadRates} disabled={loading} data-testid="fx-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {loading && !rates ? (
        <div className="p-4 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          {rates &&
            Object.entries(rates.rates || {}).map(([cur, rate]) => (
              <div
                key={cur}
                className="rounded border border-slate-200 px-3 py-2 bg-slate-50"
                data-testid={`fx-rate-${cur}`}
              >
                <div className="text-[10px] uppercase text-slate-500">{cur} → CHF</div>
                <div className="text-sm font-mono font-semibold">{Number(rate).toFixed(4)}</div>
              </div>
            ))}
        </div>
      )}

      <form onSubmit={convert} className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto,auto] gap-2 items-end" data-testid="fx-convert-form">
        <div>
          <Label className="text-xs">Betrag</Label>
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="fx-amount-input"
          />
        </div>
        <div>
          <Label className="text-xs">Von</Label>
          <SimpleSelect
            value={from}
            onValueChange={setFrom}
            options={CURRENCY_OPTIONS}
            data-testid="fx-from-select"
          />
        </div>
        <div>
          <Label className="text-xs">Nach</Label>
          <SimpleSelect
            value={to}
            onValueChange={setTo}
            options={CURRENCY_OPTIONS}
            data-testid="fx-to-select"
          />
        </div>
        <Button type="submit" data-testid="fx-convert-button">Umrechnen</Button>
      </form>

      {converted && (
        <div
          className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"
          data-testid="fx-convert-result"
        >
          <strong>{Number(converted.amount).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {converted.from_currency}</strong>{" "}
          ={" "}
          <strong className="text-emerald-700">
            {Number(converted.converted_amount).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {converted.to_currency}
          </strong>{" "}
          <span className="text-xs text-slate-500">(Kurs vom {formatDate(converted.rate_date)})</span>
        </div>
      )}
    </Card>
  );
}

export default FxRatesWidget;
